import { useState, useCallback, useRef, useEffect } from "react";
import { Switch, Route, useLocation, Router as WouterRouter } from "wouter";
import { queryClient, apiRequest } from "./lib/queryClient";
import { nativeShare, nativeCopy, hasNativeShare, hasNativeCopy } from "@/lib/nativeBridge";
import {
  QueryClientProvider,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { ThemeProvider } from "./components/ThemeProvider";
import { MobileNav } from "./components/MobileNav";
import { PerformanceChart } from "./components/PerformanceChart";
import { ActivityFeed } from "./components/ActivityFeed";
import { PlayerProfile } from "./components/PlayerProfile";
import { EventAchievementsSection } from "./components/EventAchievementsSection";
import { LeagueCard } from "./components/LeagueCard";
import { LeagueStandings } from "./components/LeagueStandings";
import { MatchRecordForm } from "./components/MatchRecordForm";
import { SearchPlayers } from "./components/SearchPlayers";
import { FriendsList } from "./components/FriendsList";
import { FriendRequests } from "./components/FriendRequests";
import { PlayerOnboarding } from "./components/PlayerOnboarding";
import { ClubOnboarding } from "./components/ClubOnboarding";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { LevelEvolutionChart } from "./components/LevelEvolutionChart";
import { AdvancedStatsCombo } from "./components/AdvancedStatsCombo";
import { HeadToHead } from "./components/HeadToHead";
import { HomeHeroStats } from "./components/HomeHeroStats";
import { LeagueDetailView } from "./components/LeagueDetailView";
import { MatchSummaryDialog } from "./components/MatchSummaryDialog";
import { ScheduledMatchesList } from "./components/ScheduledMatchesList";
import { NotificationCenter } from "./components/NotificationCenter";
import { PendingMatchesAlert } from "./components/PendingMatchesAlert";
import { NotificationBlockedBanner } from "./components/NotificationBlockedBanner";
import { ContextualTooltip } from "./components/ContextualTooltip";
import logoImage from "./assets/logo.png";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ROUTE_DEFINITIONS } from "./lib/routes";
import { useAuth } from "./hooks/useAuth";
import { useAnalyticsIdentify } from "./hooks/useAnalyticsIdentify";
import { initAnalytics, track } from "./lib/analytics";
import { useSmartPolling } from "./hooks/useSmartPolling";
import { markInternalNavigation } from "./lib/navigationHistory";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trophy,
  Target,
  TrendingUp,
  Users,
  Swords,
  Calendar,
  ChevronRight,
  Building2,
  LayoutDashboard,
  Settings,
  Share2,
  Copy,
  Mail,
  LogIn,
  Search,
  Globe,
  Lock,
  UserPlus,
  Loader2,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  HelpCircle,
  Plus,
  ClipboardList,
  Trash2,
  RefreshCw,
  ArrowLeft,
  LogOut,
  Pencil,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { SiGoogle, SiApple } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { formatLevel } from "@/lib/skillLevels";
import type { LevelHistory } from "@shared/schema";
import { ThemeToggle } from "./components/ThemeToggle";
import type {
  PlayerProfile as PlayerProfileType,
  ClubProfile as ClubProfileType,
  League,
} from "@shared/schema";
import {
  getCountryName,
  hasZeroMatchesPlayed,
  getStartHomeCase,
  pickStartHomeLeague,
} from "@workspace/profile-completion";
import { StartHomeCard } from "./components/StartHomeCard";

initAnalytics();

// Leagues Section with Search
interface LeaguesSectionProps {
  leagues: (League & {
    memberCount: number;
    userRole: string;
    isFavorite: boolean;
    pendingJoinRequestsCount?: number;
  })[];
  loadingLeagues: boolean;
  selectedLeagueId: string | null;
  setSelectedLeagueId: (id: string | null) => void;
  createLeagueMutation: any;
  setLocation?: (path: string) => void;
  leagueTab?: string;
  leagueEventosSubTab?: "convocatorias" | "americanos" | "torneos";
  onLeagueTabChange?: (tab: string) => void;
  playerCountry?: string | null;
  playerNationalRank?: number | null;
}

function LeaguesSection({
  leagues,
  loadingLeagues,
  selectedLeagueId,
  setSelectedLeagueId,
  createLeagueMutation,
  setLocation: setLocationProp,
  leagueTab,
  leagueEventosSubTab,
  onLeagueTabChange,
  playerCountry,
  playerNationalRank,
}: LeaguesSectionProps) {
  const [, setLocation] = useLocation();
  const navigate = setLocationProp || setLocation;
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  const toggleFavoriteMutation = useMutation({
    mutationFn: async (leagueId: string) => {
      const response = await apiRequest(
        "POST",
        `/api/leagues/${leagueId}/favorite`,
        {},
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Error al cambiar favorito");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { data: searchResults = [], isFetching: searchLoading } = useQuery<
    (League & { memberCount: number; creatorName: string })[]
  >({
    queryKey: ["/api/leagues/search", searchQuery],
    queryFn: async () => {
      const res = await fetch(
        `/api/leagues/search?q=${encodeURIComponent(searchQuery)}`,
        {
          credentials: "include",
        },
      );
      if (!res.ok) throw new Error("Error al buscar ligas");
      return res.json();
    },
    enabled: searchQuery.length >= 2,
  });

  const joinMutation = useMutation({
    mutationFn: async (leagueId: string) => {
      const response = await apiRequest(
        "POST",
        `/api/leagues/${leagueId}/join`,
        {},
      );
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Error al unirse");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
      setSearchQuery("");

      if (data.pending) {
        toast({
          title: "Solicitud Enviada",
          description: "Un administrador debe aprobar tu solicitud para unirte",
        });
      } else {
        toast({
          title: "¡Te has unido!",
          description: "Ahora eres miembro de esta liga",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      {selectedLeagueId ? (
        <LeagueDetailView
          leagueId={selectedLeagueId}
          onClose={() => setSelectedLeagueId(null)}
          initialTab={leagueTab}
          initialEventosSubTab={leagueEventosSubTab}
          onTabChange={onLeagueTabChange}
        />
      ) : (
        <>
          {playerCountry ? (
            <button
              onClick={() => navigate("/ligas/nacional")}
              className="w-full flex items-center gap-3 p-3.5 rounded-xl border border-border bg-card hover:bg-accent transition-colors text-left group"
              data-testid="button-national-league-card"
            >
              <img
                src={`https://flagcdn.com/w40/${playerCountry.toLowerCase()}.png`}
                alt={playerCountry}
                className="h-8 w-auto rounded shadow-sm"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight truncate">
                  {`Liga Nacional · ${getCountryName(playerCountry)}`}
                </p>
                <p className="text-xs text-muted-foreground truncate">Ranking oficial por país y género</p>
              </div>
              {playerNationalRank != null && (
                <span className="text-xs font-bold text-primary">#{playerNationalRank}</span>
              )}
              <svg className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
            </button>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Tus Ligas</h2>
            <Button
              size="sm"
              onClick={() => navigate("/crear-liga")}
              data-testid="button-create-league"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Liga
            </Button>
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                Buscar Ligas Públicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre de liga..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="input-search-leagues"
                />
              </div>

              {searchQuery.length >= 2 && (
                <div className="space-y-2">
                  {searchLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : searchResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No se encontraron ligas públicas con ese nombre
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map((league) => (
                        <div
                          key={league.id}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                          data-testid={`search-result-${league.id}`}
                        >
                          <div className="p-2 rounded-full bg-primary/10">
                            <Globe className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              {league.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {league.memberCount} miembros · Creada por{" "}
                              {league.creatorName}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => joinMutation.mutate(league.id)}
                            disabled={joinMutation.isPending}
                            data-testid={`button-join-${league.id}`}
                          >
                            {joinMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <UserPlus className="h-4 w-4 mr-1" />
                                Solicitar
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <p className="text-xs text-muted-foreground text-center">
                  Escribe al menos 2 caracteres para buscar
                </p>
              )}
            </CardContent>
          </Card>

          {loadingLeagues ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <Trophy className="h-8 w-8 text-primary animate-pulse mb-4" />
                  <p className="text-muted-foreground">Cargando ligas...</p>
                </div>
              </CardContent>
            </Card>
          ) : leagues.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex flex-col items-center justify-center text-center">
                  <Users className="h-16 w-16 text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground mb-2">
                    No perteneces a ninguna liga aún
                  </p>
                  <p className="text-sm text-muted-foreground mb-4">
                    Crea tu primera liga o busca una pública para unirte
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {leagues.map((league) => (
                <LeagueCard
                  key={league.id}
                  id={league.id}
                  name={league.name}
                  imageUrl={league.imageUrl || undefined}
                  memberCount={league.memberCount}
                  isPrivate={league.isPrivate ?? true}
                  creatorName={
                    league.userRole === "owner"
                      ? "Dueño"
                      : league.userRole === "admin"
                        ? "Admin"
                        : "Miembro"
                  }
                  isFavorite={league.isFavorite}
                  pendingJoinRequestsCount={
                    league.userRole === "owner" || league.userRole === "admin"
                      ? league.pendingJoinRequestsCount ?? 0
                      : 0
                  }
                  onView={(id) => setSelectedLeagueId(id)}
                  onToggleFavorite={(id) => toggleFavoriteMutation.mutate(id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function Dashboard() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("league") || null;
  });
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && ["home", "matches", "leagues", "social", "profile"].includes(tabParam)) {
      return tabParam;
    }
    if (params.get("league")) return "leagues";
    const saved = localStorage.getItem("dashboardActiveTab");
    return saved || "home";
  });
  const [leagueTab, setLeagueTab] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    // "callups" was renamed to "eventos" — normalise legacy deep-links
    const raw = params.get("leagueTab") || "standings";
    return raw === "callups" ? "eventos" : raw;
  });
  // When a tournament notification is clicked we pre-focus the Torneos
  // sub-tab inside the league's Eventos tab. Cleared when the league
  // detail is closed so normal navigation defaults to Convocatorias.
  const [leagueEventosSubTab, setLeagueEventosSubTab] = useState<
    "convocatorias" | "americanos" | "torneos" | undefined
  >(undefined);
  const [scrollToPending, setScrollToPending] = useState(false);
  const [matchSummary, setMatchSummary] = useState<any>(null);
  // When a notification deep link asks the user to register a match
  // result, we hold the target match id here and pass it down to
  // `ScheduledMatchesList`, which auto-opens its score dialog.
  const [resultDeepLinkMatchId, setResultDeepLinkMatchId] = useState<
    string | null
  >(() => {
    const params = new URLSearchParams(window.location.search);
    if (
      params.get("tab") === "matches" &&
      params.get("action") === "register-result"
    ) {
      return params.get("match");
    }
    return null;
  });
  const [matchesSubTab, setMatchesSubTab] = useState<
    "upcoming" | "pending" | "history"
  >(() => {
    const params = new URLSearchParams(window.location.search);
    const subtab = params.get("subtab");
    if (subtab && ["upcoming", "pending", "history"].includes(subtab)) {
      return subtab as "upcoming" | "pending" | "history";
    }
    return "upcoming";
  });
  const [matchToDelete, setMatchToDelete] = useState<{
    id: string;
    type: "league" | "global";
    leagueId?: string;
  } | null>(null);
  const pendingMatchesRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    let pendingCode: string | null = null;

    const lsCode = localStorage.getItem('pendingInviteCode');
    const lsTs = localStorage.getItem('pendingInviteTimestamp');
    if (lsCode) {
      const age = lsTs ? Date.now() - parseInt(lsTs, 10) : Infinity;
      localStorage.removeItem('pendingInviteCode');
      localStorage.removeItem('pendingInviteTimestamp');
      if (age < 15 * 60 * 1000) {
        pendingCode = lsCode;
      }
    }

    if (!pendingCode) {
      const cookieMatch = document.cookie.match(/(?:^|;\s*)pendingInvite=([^;]+)/);
      if (cookieMatch) {
        pendingCode = decodeURIComponent(cookieMatch[1]);
        document.cookie = 'pendingInvite=; Max-Age=0; path=/';
      }
    }

    if (pendingCode && !window.location.pathname.startsWith('/join/')) {
      setLocation(`/join/${pendingCode}`);
    }
  }, []);

  // Scroll/highlight a specific match card (pending or upcoming) once it renders.
  const focusMatchCard = useCallback((matchId: string) => {
    let attempts = 0;
    const tryFocus = () => {
      const selectors = [
        `[data-testid="pending-match-card-${matchId}"]`,
        `[data-testid="scheduled-match-${matchId}"]`,
        `[data-testid="upcoming-match-${matchId}"]`,
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel) as HTMLElement | null;
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-primary", "ring-offset-2");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-primary", "ring-offset-2");
          }, 2500);
          return;
        }
      }
      attempts += 1;
      if (attempts < 20) setTimeout(tryFocus, 150);
    };
    setTimeout(tryFocus, 100);
  }, []);

  // Handle a match deep-link: open the summary page for completed matches,
  // or scroll to the relevant card when sitting on a pending/upcoming subtab.
  const handleMatchDeepLink = useCallback(
    (matchId: string, subtab: string | null) => {
      if (subtab === "pending" || subtab === "upcoming") {
        focusMatchCard(matchId);
      } else {
        setLocation(`/resumen-partido/${matchId}`);
      }
    },
    [focusMatchCard, setLocation],
  );

  // Initial load: if landed on /?tab=matches&match=<id>, route to the match.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const matchId = params.get("match");
    if (matchId && params.get("tab") === "matches") {
      if (params.get("action") === "register-result") {
        // For the "registra el resultado" reminder two things happen in
        // parallel:
        //   1. `resultDeepLinkMatchId` is already set from the URL by its
        //      useState initializer and passed down to `ScheduledMatchesList`,
        //      which auto-opens the score dialog once the API data resolves.
        //   2. We kick off a scroll-to-card retry loop here so that the card
        //      gets highlighted as spatial context (before or after the dialog
        //      opens, whichever wins the race). `ScheduledMatchesList` also
        //      has its own direct scroll fallback for non-scheduleable statuses
        //      (e.g. score_pending) in case this retry loop expires first.
        focusMatchCard(matchId);
      } else {
        handleMatchDeepLink(matchId, params.get("subtab"));
      }
    }
    // Only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleSwMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE' && event.data.url) {
        const url = new URL(event.data.url, window.location.origin);
        // Full path links (e.g. callup detail) should navigate via the router.
        if (url.pathname && url.pathname !== '/' && url.pathname !== '') {
          setLocation(url.pathname + url.search);
          return;
        }
        const params = new URLSearchParams(url.search);
        const tab = params.get("tab");
        if (tab && ["home", "matches", "leagues", "social", "profile"].includes(tab)) {
          setActiveTab(tab);
        }
        const subtab = params.get("subtab");
        if (subtab && ["upcoming", "pending", "history"].includes(subtab)) {
          setMatchesSubTab(subtab as "upcoming" | "pending" | "history");
        }
        const league = params.get("league");
        if (league) {
          setSelectedLeagueId(league);
        }
        const lt = params.get("leagueTab");
        if (lt) {
          // "callups" was renamed to "eventos" — normalise legacy push/SW deep-links
          setLeagueTab(lt === "callups" ? "eventos" : lt);
        }
        const matchId = params.get("match");
        const action = params.get("action");
        if (matchId && tab === "matches") {
          if (action === "register-result") {
            // Auto-open the score dialog. We still scroll to the card
            // so users have spatial context if they close the dialog.
            setMatchesSubTab("upcoming");
            setResultDeepLinkMatchId(matchId);
            focusMatchCard(matchId);
          } else {
            handleMatchDeepLink(matchId, subtab);
          }
        }
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, [handleMatchDeepLink, setLocation]);

  useEffect(() => {
    const newParams = new URLSearchParams();
    if (selectedLeagueId) {
      newParams.set("league", selectedLeagueId);
      if (leagueTab && leagueTab !== "standings") {
        newParams.set("leagueTab", leagueTab);
      }
    }
    const qs = newParams.toString();
    window.history.replaceState({}, "", qs ? `/?${qs}` : "/");
  }, [selectedLeagueId, leagueTab]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("dashboardActiveTab", activeTab);
  }, [activeTab]);

  // Scroll to pending matches section when coming from home notification
  useEffect(() => {
    if (
      scrollToPending &&
      activeTab === "matches" &&
      pendingMatchesRef.current
    ) {
      setTimeout(() => {
        pendingMatchesRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
        setScrollToPending(false);
      }, 100);
    }
  }, [scrollToPending, activeTab]);

  // Get real user profile data. The server attaches `homeClubName` to
  // the player profile payload (task #180) so the Profile screen can
  // render the user's home club without an extra round trip.
  const playerProfile = user?.profile as
    | (PlayerProfileType & { homeClubName?: string | null })
    | null;

  // Fetch leagues
  const {
    data: leagues = [],
    isLoading: loadingLeagues,
    refetch: refetchLeagues,
  } = useQuery<
    (League & {
      memberCount: number;
      userRole: string;
      isFavorite: boolean;
      pendingJoinRequestsCount?: number;
    })[]
  >({
    queryKey: ["/api/leagues"],
  });

  // Fetch all pending matches for the user across all leagues
  interface PendingMatch {
    id: string;
    leagueId?: string;
    leagueName?: string;
    isGlobalMatch?: boolean;
    team1Player1Id: string;
    team1Player1Name: string;
    team1Player2Id?: string;
    team1Player2Name?: string;
    team2Player1Id: string;
    team2Player1Name: string;
    team2Player2Id?: string;
    team2Player2Name?: string;
    winner: 1 | 2;
    score?: string | { sets: Array<{ team1: number; team2: number }> };
    status: "pending" | "approved" | "rejected";
    reporterTeam: 1 | 2;
    reportedById: string;
    reportedByName?: string | null;
    recordedById?: string | null;
    recordedByName?: string | null;
    ratingChanges?: {
      team1Player1: { before: number; after: number; delta: number };
      team1Player2?: { before: number; after: number; delta: number };
      team2Player1: { before: number; after: number; delta: number };
      team2Player2?: { before: number; after: number; delta: number };
    };
    playedAt: string;
    createdAt: string;
  }

  const AUTO_APPROVAL_HOURS = 3;
  function formatDeadlineTime(createdAt: string): string {
    const deadline = new Date(new Date(createdAt).getTime() + AUTO_APPROVAL_HOURS * 60 * 60 * 1000);
    return deadline.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", hour12: true });
  }

  // Query league pending matches
  const { data: leaguePendingMatches = [] } = useQuery<PendingMatch[]>({
    queryKey: ["/api/matches/pending"],
  });

  // Query global pending matches
  const { data: globalPendingMatches = [] } = useQuery<PendingMatch[]>({
    queryKey: ["/api/matches/global/pending"],
  });

  // Combine both types of pending matches
  const allPendingMatches: PendingMatch[] = [
    ...leaguePendingMatches,
    ...globalPendingMatches.map((m) => ({
      ...m,
      isGlobalMatch: true,
      leagueName: "Partido Libre",
    })),
  ];

  const { data: levelHistory = [] } = useQuery<LevelHistory[]>({
    queryKey: ["/api/profile/level-history"],
  });

  // Fetch match history for charts and activity feed
  interface MatchHistoryItem {
    id: string;
    type: "league" | "global";
    leagueId?: string;
    leagueName?: string;
    homeClubId?: string | null;
    homeClubName?: string | null;
    playedAt: string;
    status: string;
    winner: number;
    score: any;
    userTeam: number;
    userWon: boolean;
    ratingBefore: number;
    ratingAfter: number;
    ratingDelta: number;
    opponents: string[];
    teammate?: string;
  }

  const { data: matchHistory = [] } = useQuery<MatchHistoryItem[]>({
    queryKey: ["/api/profile/match-history"],
  });

  // "Home de arranque" (task #495): players with 0 matches (approved or
  // pending) see a simplified, action-oriented Home instead of the regular
  // stats dashboard. See lib/profile-completion for the shared case logic.
  const isStartHome = hasZeroMatchesPlayed({
    approvedMatchesCount: matchHistory.length,
    pendingMatches: allPendingMatches,
    userId: user?.id,
  });

  const { data: nationalStats } = useQuery<{
    totalActive: number;
    totalInactive: number;
  }>({
    queryKey: ["/api/national-ranking/stats"],
    enabled: isStartHome && !!playerProfile?.country,
  });
  const nationalPlayerCount = nationalStats
    ? nationalStats.totalActive + nationalStats.totalInactive
    : undefined;

  const startHomeCase = isStartHome ? getStartHomeCase(leagues) : "A";
  const startHomeFeaturedLeague = isStartHome
    ? pickStartHomeLeague(leagues, startHomeCase)
    : null;
  const startHomeLeagueRows = isStartHome
    ? [
        ...leagues.map((l) => ({
          id: l.id,
          name: l.name,
          memberCount: l.memberCount,
        })),
        ...(playerProfile?.country
          ? [
              {
                id: "national",
                name: `Liga Nacional · ${getCountryName(playerProfile.country)}`,
                memberCount: nationalPlayerCount ?? 0,
                isNational: true,
              },
            ]
          : []),
      ]
    : [];

  // Filter to only show matches the user can approve (they are on the opposing team from reporter)
  const approvableMatches = allPendingMatches.filter((match) => {
    const isTeam1 =
      user?.id === match.team1Player1Id || user?.id === match.team1Player2Id;
    const isTeam2 =
      user?.id === match.team2Player1Id || user?.id === match.team2Player2Id;

    // User must be a player in the match
    if (!isTeam1 && !isTeam2) return false;

    // User must be on the opposing team from the reporter
    const userTeam = isTeam1 ? 1 : 2;
    return userTeam !== match.reporterTeam;
  });

  // Approve/reject match mutation (supports both league and global matches)
  const approveMatchMutation = useMutation({
    mutationFn: async ({
      leagueId,
      matchId,
      action,
      isGlobalMatch,
    }: {
      leagueId?: string;
      matchId: string;
      action: "approve" | "reject";
      isGlobalMatch?: boolean;
    }) => {
      const endpoint = isGlobalMatch
        ? `/api/matches/global/${matchId}/approve`
        : `/api/leagues/${leagueId}/matches/${matchId}/approve`;
      const response = await apiRequest("POST", endpoint, { action });
      return response.json();
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ["/api/matches/pending"] });
      await queryClient.cancelQueries({ queryKey: ["/api/matches/global/pending"] });

      const prevLeaguePending = queryClient.getQueryData<PendingMatch[]>(["/api/matches/pending"]);
      const prevGlobalPending = queryClient.getQueryData<PendingMatch[]>(["/api/matches/global/pending"]);

      if (variables.isGlobalMatch) {
        queryClient.setQueryData<PendingMatch[]>(["/api/matches/global/pending"], (old) =>
          (old || []).filter((m) => m.id !== variables.matchId)
        );
      } else {
        queryClient.setQueryData<PendingMatch[]>(["/api/matches/pending"], (old) =>
          (old || []).filter((m) => m.id !== variables.matchId)
        );
      }

      return { prevLeaguePending, prevGlobalPending };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/matches/global/pending"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profile/level-history"] });
      if (variables.leagueId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/leagues", variables.leagueId],
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      toast({
        title:
          variables.action === "approve"
            ? "¡Partido Confirmado!"
            : "Partido Rechazado",
        description:
          variables.action === "approve"
            ? "Los rankings han sido actualizados."
            : "El partido ha sido rechazado.",
      });
    },
    onError: (_, variables, context) => {
      if (context?.prevLeaguePending) {
        queryClient.setQueryData(["/api/matches/pending"], context.prevLeaguePending);
      }
      if (context?.prevGlobalPending) {
        queryClient.setQueryData(["/api/matches/global/pending"], context.prevGlobalPending);
      }
      toast({
        title: "Error",
        description: "No se pudo procesar el partido.",
        variant: "destructive",
      });
    },
  });

  // Delete match mutation (supports both league and global matches)
  const deleteMatchMutation = useMutation({
    mutationFn: async ({
      matchId,
      matchType,
      leagueId,
    }: {
      matchId: string;
      matchType: "league" | "global";
      leagueId?: string;
    }) => {
      const endpoint =
        matchType === "global"
          ? `/api/matches/global/${matchId}`
          : `/api/leagues/${leagueId}/matches/${matchId}`;
      const response = await apiRequest("DELETE", endpoint);
      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: "Error desconocido" }));
        throw new Error(error.message);
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate all relevant caches
      queryClient.invalidateQueries({
        queryKey: ["/api/profile/match-history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/profile/level-history"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/matches/pending"] });
      queryClient.invalidateQueries({
        queryKey: ["/api/matches/global/pending"],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
      if (variables.leagueId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/leagues", variables.leagueId],
        });
      }
      toast({
        title: "Partido Eliminado",
        description:
          "El partido ha sido eliminado y los niveles/estadísticas han sido revertidos.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el partido.",
        variant: "destructive",
      });
    },
  });

  // Create league mutation
  const createLeagueMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      description: string;
      isPrivate: boolean;
      matchFormat: string;
      imageUrl?: string;
      homeClubId?: string | null;
    }) => {
      const response = await apiRequest("POST", "/api/leagues", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
      toast({
        title: "¡Liga Creada!",
        description:
          "Tu liga ha sido creada exitosamente. ¡Comparte el link de invitación!",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo crear la liga. Intenta de nuevo.",
        variant: "destructive",
      });
    },
  });

  const handleLogout = () => {
    import("@/lib/sessionPersistence").then(({ logout }) => logout());
  };

  // Memoized function to fetch league members - prevents infinite loop in MatchRecordForm
  const handleLeagueChange = useCallback(async (leagueId: string) => {
    try {
      const response = await fetch(`/api/leagues/${leagueId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.members && Array.isArray(data.members)) {
          return data.members
            .filter(
              (m: { userId: string; userName: string; rating: number }) =>
                m.userId,
            )
            .map((m: { userId: string; userName: string; rating: number }) => ({
              id: m.userId,
              name: m.userName || "Jugador",
              rating: m.rating || 1000,
            }));
        }
      }
    } catch (error) {
      console.error("Error fetching league members:", error);
    }
    return [];
  }, []);

  // Pending friend requests count — drives the Social tab badge on mobile
  // nav and the desktop tab button. Uses the same cache key as the
  // FriendRequests component, so no extra API call is made.
  const { data: friendRequestsData } = useQuery<{ id: string }[]>({
    queryKey: ['/api/friends/requests'],
    enabled: !!user,
  });
  const rawFriendRequestCount = friendRequestsData?.length ?? 0;

  // Badge-clear logic: track the count at the moment the user last
  // opened the Social tab. Badge shows only when the current count
  // exceeds that snapshot. Resets to 0 when count drops to 0 so a
  // subsequent new request always triggers the badge again.
  const [lastSeenRequestCount, setLastSeenRequestCount] = useState(0);
  useEffect(() => {
    if (rawFriendRequestCount === 0) {
      setLastSeenRequestCount(0);
    }
  }, [rawFriendRequestCount]);
  useEffect(() => {
    if (activeTab === "social") {
      setLastSeenRequestCount(rawFriendRequestCount);
    }
  }, [activeTab, rawFriendRequestCount]);
  const socialBadgeCount =
    rawFriendRequestCount > lastSeenRequestCount ? rawFriendRequestCount : 0;

  const desktopTabs = [
    { id: "home", label: "Inicio", icon: LayoutDashboard },
    { id: "matches", label: "Partidos", icon: Swords },
    { id: "leagues", label: "Ligas", icon: Trophy },
    { id: "social", label: "Social", icon: Users },
    { id: "profile", label: "Perfil", icon: Settings },
  ];

  const handleViewPendingMatches = useCallback(() => {
    setActiveTab("matches");
    setMatchesSubTab("pending");
    setTimeout(() => {
      pendingMatchesRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }, []);

  return (
    <div className="min-h-screen bg-background pb-14 lg:pb-0">
      <NotificationBlockedBanner />
      <PendingMatchesAlert 
        approvableCount={approvableMatches.length} 
        onViewPending={handleViewPendingMatches}
      />
      <header
        className="sticky top-0 z-40 bg-background border-b border-border"
        style={{ paddingTop: "calc(0.5rem + env(safe-area-inset-top, 0px))" }}
      >
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src={logoImage}
                alt="CourtVibe"
                className="h-10 w-10 rounded-lg shadow-md group-hover:shadow-lg transition-shadow"
              />
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-primary/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
            </div>
            <span className="font-bold text-lg tracking-tight text-primary">
              CourtVibe
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              {user?.firstName || user?.email}
            </span>
            <NotificationCenter
              onNavigate={setActiveTab}
              onOpenMatchResultDialog={(matchId) => {
                setMatchesSubTab("upcoming");
                setResultDeepLinkMatchId(matchId);
              }}
              onOpenLeagueEvents={(leagueId) => {
                setActiveTab("leagues");
                setSelectedLeagueId(leagueId);
                setLeagueTab("eventos");
                setLeagueEventosSubTab("torneos");
              }}
              onOpenLeagueJoinRequests={(leagueId) => {
                setActiveTab("leagues");
                setSelectedLeagueId(leagueId);
                // The pending-request card is shown at the top of the
                // league detail view, above the tabs.
                setLeagueTab("standings");
              }}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => queryClient.refetchQueries()}
                  data-testid="button-refresh"
                  aria-label="Actualizar datos"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Actualizar datos</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleLogout}
                  data-testid="button-logout"
                  aria-label="Cerrar sesión"
                >
                  <LogOut className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cerrar sesión</TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Desktop Navigation Tabs */}
        <nav className="hidden lg:flex items-center gap-1 px-4 pb-2">
          {desktopTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const showBadge = tab.id === "social" && socialBadgeCount > 0;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
                data-testid={`desktop-nav-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {showBadge && (
                  <span
                    className="ml-1 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none px-1"
                    data-testid="badge-desktop-social-requests"
                  >
                    {socialBadgeCount > 99 ? "99+" : socialBadgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="p-4 lg:pb-4 max-w-7xl mx-auto safe-area-bottom">
        {activeTab === "home" && (
          <div className="space-y-6">
            <ContextualTooltip section="home" tooltipsSeen={user?.tooltipsSeen} />
            {isStartHome ? (
              <StartHomeCard
                startHomeCase={startHomeCase}
                playerCountry={playerProfile?.country}
                nationalPlayerCount={nationalPlayerCount}
                skillLevel={playerProfile?.skillLevel}
                featuredLeagueName={startHomeFeaturedLeague?.name}
                leagueRows={startHomeLeagueRows}
                onCreateLeague={() => setLocation("/crear-liga")}
                onJoinByCode={() => {
                  const code = window.prompt(
                    "Ingresa el código de invitación de la liga",
                  );
                  if (code && code.trim()) {
                    setLocation(`/join/${code.trim()}`);
                  }
                }}
                onExploreNational={() => setLocation("/ligas/nacional")}
                onInviteLeague={() => {
                  if (startHomeFeaturedLeague) {
                    setSelectedLeagueId(startHomeFeaturedLeague.id);
                    setActiveTab("leagues");
                  }
                }}
                onRegisterMatch={() =>
                  setLocation(
                    startHomeFeaturedLeague
                      ? `/registrar-partido?leagueId=${startHomeFeaturedLeague.id}`
                      : "/registrar-partido",
                  )
                }
                onViewLeague={(leagueId) => {
                  if (leagueId === "national") {
                    setLocation("/ligas/nacional");
                    return;
                  }
                  setSelectedLeagueId(leagueId);
                  setActiveTab("leagues");
                }}
              />
            ) : (
              <>
                {/* Hero Stats Section - Prominent and Energetic */}
                <HomeHeroStats
                  playerProfile={
                    levelHistory && levelHistory.length > 0
                      ? ({
                          ...playerProfile,
                          skillLevel: levelHistory[0].newLevel,
                        } as any)
                      : playerProfile
                  }
                  matchHistory={matchHistory}
                  onInfoClick={() => setLocation("/como-funciona")}
                  onCardClick={() => setLocation("/nivel-evolucion")}
                />
              </>
            )}

            {/* Pending Matches Notification */}
            {approvableMatches.length > 0 && (
              <Card
                className="border-secondary/50 bg-secondary/5 dark:bg-secondary/10 cursor-pointer hover-elevate"
                onClick={() => {
                  setActiveTab("matches");
                }}
                data-testid="card-pending-matches-alert"
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-full bg-secondary/20">
                      <AlertCircle className="h-6 w-6 text-secondary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-secondary">
                        {approvableMatches.length}{" "}
                        {approvableMatches.length === 1
                          ? "partido pendiente"
                          : "partidos pendientes"}{" "}
                        de confirmación
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Toca aquí para confirmar o rechazar los resultados
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-secondary" />
                  </div>
                </CardContent>
              </Card>
            )}

            {!isStartHome && (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* Upcoming Matches */}
                <Card data-testid="card-upcoming-matches">
                  <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-primary" />
                      Próximos Partidos
                    </CardTitle>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setLocation("/agendar-partido")}
                      data-testid="button-schedule-match-home"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Agendar
                    </Button>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <ScheduledMatchesList
                      userId={user?.id}
                      limit={5}
                      showViewAll={true}
                      onViewAll={() => {
                        setActiveTab("matches");
                        setMatchesSubTab("upcoming");
                      }}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
            )}
          </div>
        )}

        {activeTab === "matches" && (
          <div className="space-y-4">
            <ContextualTooltip section="matches" tooltipsSeen={user?.tooltipsSeen} />
            {/* Compact Header with Primary Actions */}
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold">Partidos</h2>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setLocation("/agendar-partido")}
                  data-testid="button-schedule-match"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  Agendar
                </Button>
                <Button
                  size="sm"
                  onClick={() => setLocation("/registrar-partido")}
                  data-testid="button-record-match"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Registrar
                </Button>
              </div>
            </div>

            {/* Segmented Navigation */}
            <div className="flex p-1 bg-muted rounded-lg">
              <button
                onClick={() => setMatchesSubTab("upcoming")}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  matchesSubTab === "upcoming"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="tab-upcoming"
              >
                <Calendar className="h-4 w-4 inline-block mr-1" />
                Próximos
              </button>
              <button
                onClick={() => {
                  setMatchesSubTab("pending");
                  setScrollToPending(true);
                }}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors relative ${
                  matchesSubTab === "pending"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="tab-pending"
              >
                <Clock className="h-4 w-4 inline-block mr-1" />
                Pendientes
                {allPendingMatches.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 w-5 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center">
                    {allPendingMatches.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setMatchesSubTab("history")}
                className={`flex-1 py-2 px-3 text-sm font-medium rounded-md transition-colors ${
                  matchesSubTab === "history"
                    ? "bg-background shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                data-testid="tab-history"
              >
                <ClipboardList className="h-4 w-4 inline-block mr-1" />
                Historial
              </button>
            </div>

            {/* Content based on selected sub-tab */}
            {matchesSubTab === "upcoming" && (
              <div className="space-y-4">
                {user?.id && (
                  <ScheduledMatchesList
                    userId={user.id}
                    resultDeepLinkMatchId={resultDeepLinkMatchId}
                    onResultDeepLinkConsumed={() =>
                      setResultDeepLinkMatchId(null)
                    }
                  />
                )}
                {/* Empty state */}
                {!user?.id && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Calendar className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">
                      No tienes partidos agendados
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => setLocation("/agendar-partido")}
                    >
                      Agendar partido
                    </Button>
                  </div>
                )}
              </div>
            )}

            {matchesSubTab === "pending" && (
              <div ref={pendingMatchesRef} className="space-y-3">
                {allPendingMatches.length > 0 ? (
                  allPendingMatches.map((match) => {
                    const team1Names = [match.team1Player1Name];
                    if (match.team1Player2Name)
                      team1Names.push(match.team1Player2Name);

                    const team2Names = [match.team2Player1Name];
                    if (match.team2Player2Name)
                      team2Names.push(match.team2Player2Name);

                    const isTeam1 =
                      user?.id === match.team1Player1Id ||
                      user?.id === match.team1Player2Id;
                    const isTeam2 =
                      user?.id === match.team2Player1Id ||
                      user?.id === match.team2Player2Id;
                    const userTeam = isTeam1 ? 1 : 2;
                    const canApprove =
                      (isTeam1 || isTeam2) && userTeam !== match.reporterTeam;
                    const isReporter = user?.id === match.reportedById;
                    const deadlineTime = formatDeadlineTimeWeb(match.createdAt);

                    const pendingTeam1Change =
                      match.ratingChanges?.team1Player1?.delta || 0;
                    const pendingTeam2Change =
                      match.ratingChanges?.team2Player1?.delta || 0;

                    const getScoreSets = (
                      score:
                        | string
                        | { sets: Array<{ team1: number; team2: number }> }
                        | undefined,
                    ): Array<{ team1: number; team2: number }> => {
                      if (!score) return [];
                      if (typeof score === "string") {
                        const parts = score.split(",").map((s) => s.trim());
                        return parts.map((part) => {
                          const [t1, t2] = part.split("-").map(Number);
                          return { team1: t1 || 0, team2: t2 || 0 };
                        });
                      }
                      if (
                        typeof score === "object" &&
                        score.sets &&
                        Array.isArray(score.sets)
                      ) {
                        return score.sets;
                      }
                      return [];
                    };

                    const scoreSets = getScoreSets(match.score);

                    const wasEdited =
                      !!match.recordedById &&
                      !!match.reportedById &&
                      match.recordedById !== match.reportedById;
                    const editorName = match.reportedByName || null;

                    return (
                      <Card
                        key={match.id}
                        className="border-orange-200 dark:border-orange-900/50 overflow-hidden"
                        data-testid={`pending-match-card-${match.id}`}
                      >
                        {/* Header with league name */}
                        <div className="px-4 py-2 bg-orange-50 dark:bg-orange-950/30 border-b border-orange-100 dark:border-orange-900/30">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-xs font-medium text-orange-700 dark:text-orange-400">
                              <Trophy className="h-3 w-3" />
                              {match.leagueName || "Partido Libre"}
                            </span>
                            <Badge className="bg-orange-500 text-white text-xs px-2 py-0.5">
                              Pendiente
                            </Badge>
                          </div>
                          {wasEdited && editorName && (
                            <div
                              className="flex items-center gap-1 text-xs text-muted-foreground italic mt-1"
                              data-testid={`text-edited-by-${match.id}`}
                            >
                              <Pencil className="h-3 w-3" />
                              Editado por {editorName}
                            </div>
                          )}
                        </div>

                        <CardContent className="p-4 space-y-3">
                          {/* Teams with scores on the right */}
                          <div className="space-y-2">
                            {/* Team 1 */}
                            <div
                              className={`p-3 rounded-lg border-2 transition-all ${
                                match.winner === 1
                                  ? "border-orange-400 bg-orange-100/50 dark:bg-orange-900/30"
                                  : "border-muted"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {match.winner === 1 && (
                                    <Trophy className="h-4 w-4 text-orange-500 shrink-0" />
                                  )}
                                  <span className="font-medium text-sm truncate">
                                    {team1Names.join(" / ")}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {pendingTeam1Change !== 0 && (
                                    <span
                                      className={`text-xs font-medium opacity-70 ${
                                        pendingTeam1Change > 0
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {pendingTeam1Change > 0 ? "+" : ""}
                                      {pendingTeam1Change}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-1">
                                    {scoreSets.map((set, idx) => (
                                      <span
                                        key={idx}
                                        className={`font-bold text-base tabular-nums px-1.5 py-0.5 rounded ${
                                          set.team1 > set.team2
                                            ? "bg-orange-200/50 dark:bg-orange-800/30"
                                            : ""
                                        }`}
                                      >
                                        {set.team1}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Team 2 */}
                            <div
                              className={`p-3 rounded-lg border-2 transition-all ${
                                match.winner === 2
                                  ? "border-orange-400 bg-orange-100/50 dark:bg-orange-900/30"
                                  : "border-muted"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  {match.winner === 2 && (
                                    <Trophy className="h-4 w-4 text-orange-500 shrink-0" />
                                  )}
                                  <span className="font-medium text-sm truncate">
                                    {team2Names.join(" / ")}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  {pendingTeam2Change !== 0 && (
                                    <span
                                      className={`text-xs font-medium opacity-70 ${
                                        pendingTeam2Change > 0
                                          ? "text-green-600"
                                          : "text-red-600"
                                      }`}
                                    >
                                      {pendingTeam2Change > 0 ? "+" : ""}
                                      {pendingTeam2Change}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-1">
                                    {scoreSets.map((set, idx) => (
                                      <span
                                        key={idx}
                                        className={`font-bold text-base tabular-nums px-1.5 py-0.5 rounded ${
                                          set.team2 > set.team1
                                            ? "bg-orange-200/50 dark:bg-orange-800/30"
                                            : ""
                                        }`}
                                      >
                                        {set.team2}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Action buttons */}
                          {canApprove ? (
                            <>
                              <div className="flex gap-2 pt-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                                  onClick={() =>
                                    approveMatchMutation.mutate({
                                      leagueId: match.leagueId,
                                      matchId: match.id,
                                      action: "reject",
                                      isGlobalMatch: match.isGlobalMatch,
                                    })
                                  }
                                  disabled={approveMatchMutation.isPending}
                                  data-testid={`button-reject-${match.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Rechazar
                                </Button>
                                <Button
                                  size="sm"
                                  className="flex-1 bg-green-600 hover:bg-green-700"
                                  onClick={() =>
                                    approveMatchMutation.mutate({
                                      leagueId: match.leagueId,
                                      matchId: match.id,
                                      action: "approve",
                                      isGlobalMatch: match.isGlobalMatch,
                                    })
                                  }
                                  disabled={approveMatchMutation.isPending}
                                  data-testid={`button-approve-${match.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  Confirmar
                                </Button>
                              </div>
                              {match.createdAt ? (
                                <p className="text-xs text-center text-muted-foreground pt-1">
                                  <Clock className="inline h-3 w-3 mr-1" />
                                  Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                </p>
                              ) : null}
                            </>
                          ) : isReporter ? (
                            <div className="space-y-2 pt-1">
                              <p className="text-xs text-center text-muted-foreground">
                                Esperando confirmación del equipo contrario
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1"
                                  onClick={() => setLocation(`/editar-partido/${match.id}${match.isGlobalMatch ? '?type=global' : `?type=league&leagueId=${match.leagueId}`}`)}
                                  data-testid={`button-edit-pending-${match.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-1" />
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="flex-1 text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                                  onClick={() => setMatchToDelete({
                                    id: match.id,
                                    type: match.isGlobalMatch ? 'global' : 'league',
                                    leagueId: match.leagueId,
                                  })}
                                  data-testid={`button-delete-pending-${match.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Eliminar
                                </Button>
                              </div>
                              {match.createdAt ? (
                                <p className="text-xs text-center text-muted-foreground pt-1">
                                  <Clock className="inline h-3 w-3 mr-1" />
                                  Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                </p>
                              ) : null}
                            </div>
                          ) : (
                            <>
                              <p className="text-xs text-center text-muted-foreground pt-1">
                                Este partido está pendiente de confirmación
                              </p>
                              {match.createdAt ? (
                                <p className="text-xs text-center text-muted-foreground pt-1">
                                  <Clock className="inline h-3 w-3 mr-1" />
                                  Se aprueba automáticamente a las {formatDeadlineTime(match.createdAt)}
                                </p>
                              ) : null}
                            </>
                          )}
                          {deadlineTime && (
                            <p className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                              <Clock className="h-3 w-3 shrink-0" />
                              Se aprueba automáticamente a las {deadlineTime}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CheckCircle className="h-12 w-12 text-green-500/30 mb-3" />
                    <p className="text-muted-foreground text-sm">
                      No hay partidos pendientes
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Los partidos que necesitan confirmación aparecerán aquí
                    </p>
                  </div>
                )}
              </div>
            )}

            {matchesSubTab === "history" && (
              <div className="space-y-3">
                {matchHistory.length > 0 ? (
                  matchHistory.slice(0, 15).map((match, index) => {
                    const getScoreSets = (
                      score: any,
                    ): Array<{ team1: number; team2: number }> => {
                      if (!score) return [];
                      if (typeof score === "string") {
                        const parts = score.split(",").map((s) => s.trim());
                        return parts.map((part) => {
                          const [t1, t2] = part.split("-").map(Number);
                          return { team1: t1 || 0, team2: t2 || 0 };
                        });
                      }
                      if (score.sets && Array.isArray(score.sets)) {
                        return score.sets;
                      }
                      return [];
                    };

                    const formatDate = (dateStr: string): string => {
                      try {
                        const date = new Date(dateStr);
                        return date.toLocaleDateString("es-DO", {
                          day: "numeric",
                          month: "short",
                          year:
                            date.getFullYear() !== new Date().getFullYear()
                              ? "numeric"
                              : undefined,
                        });
                      } catch {
                        return dateStr;
                      }
                    };

                    const scoreSets = getScoreSets(match.score);
                    const userName = user?.firstName || "Tú";
                    const teammateDisplay = match.teammate
                      ? `${userName} / ${match.teammate}`
                      : userName;

                    return (
                      <Card
                        key={match.id || index}
                        className="overflow-hidden cursor-pointer hover-elevate"
                        onClick={() => {
                          setLocation(`/resumen-partido/${match.id}`);
                        }}
                        data-testid={`history-match-${index}`}
                      >
                        {/* Header with date and league */}
                        <div className="px-4 py-2 bg-muted/50 border-b flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                            <Calendar className="h-3 w-3 shrink-0" />
                            <span className="shrink-0">{formatDate(match.playedAt)}</span>
                            {match.homeClubName ? (
                              <>
                                <span className="shrink-0">·</span>
                                <span
                                  className="truncate"
                                  data-testid={`text-history-home-club-${index}`}
                                >
                                  {match.homeClubName}
                                </span>
                              </>
                            ) : null}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {match.type === "league" && match.leagueName && (
                              <Badge variant="outline" className="text-xs">
                                {match.leagueName.length > 20
                                  ? match.leagueName.slice(0, 20) + "..."
                                  : match.leagueName}
                              </Badge>
                            )}
                            {match.type === "global" && (
                              <Badge variant="secondary" className="text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                Global
                              </Badge>
                            )}
                          </div>
                        </div>

                        <CardContent className="p-4 space-y-3">
                          {/* Your team */}
                          <div
                            className={`p-3 rounded-lg border-2 ${
                              match.userWon
                                ? "border-green-500 bg-green-500/10"
                                : "border-muted"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {match.userWon && (
                                  <Trophy className="h-4 w-4 text-green-500 shrink-0" />
                                )}
                                <p className="font-medium text-sm truncate">
                                  {teammateDisplay}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {match.ratingDelta !== 0 && (
                                  <span
                                    className={`text-xs font-medium ${
                                      match.ratingDelta > 0
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }`}
                                  >
                                    {match.ratingDelta > 0 ? "+" : ""}
                                    {match.ratingDelta}
                                  </span>
                                )}
                                <div className="flex items-center gap-1">
                                  {scoreSets.map((set, idx) => (
                                    <span
                                      key={idx}
                                      className={`font-bold text-base tabular-nums px-1.5 py-0.5 rounded ${
                                        (
                                          match.userTeam === 1
                                            ? set.team1 > set.team2
                                            : set.team2 > set.team1
                                        )
                                          ? "bg-green-200/50 dark:bg-green-800/30"
                                          : ""
                                      }`}
                                    >
                                      {match.userTeam === 1
                                        ? set.team1
                                        : set.team2}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Opponents */}
                          <div
                            className={`p-3 rounded-lg border-2 ${
                              !match.userWon
                                ? "border-green-500 bg-green-500/10"
                                : "border-muted"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {!match.userWon && (
                                  <Trophy className="h-4 w-4 text-green-500 shrink-0" />
                                )}
                                <p className="font-medium text-sm truncate">
                                  {match.opponents.join(" / ")}
                                </p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                {scoreSets.map((set, idx) => (
                                  <span
                                    key={idx}
                                    className={`font-bold text-base tabular-nums px-1.5 py-0.5 rounded ${
                                      (
                                        match.userTeam === 1
                                          ? set.team2 > set.team1
                                          : set.team1 > set.team2
                                      )
                                        ? "bg-green-200/50 dark:bg-green-800/30"
                                        : ""
                                    }`}
                                  >
                                    {match.userTeam === 1
                                      ? set.team2
                                      : set.team1}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Delete button - only for league matches */}
                          {match.type === "league" && (
                            <div className="flex justify-end pt-2 border-t">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() =>
                                  setMatchToDelete({
                                    id: match.id,
                                    type: match.type,
                                    leagueId: match.leagueId,
                                  })
                                }
                                data-testid={`button-delete-match-${match.id}`}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Eliminar
                              </Button>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Swords className="h-12 w-12 text-muted-foreground/30 mb-3" />
                    <p className="text-muted-foreground text-sm">
                      Sin historial de partidos
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Tus partidos completados aparecerán aquí
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "leagues" && (
          <>
            <ContextualTooltip section="leagues" tooltipsSeen={user?.tooltipsSeen} />
            <LeaguesSection
              leagues={leagues}
              loadingLeagues={loadingLeagues}
              selectedLeagueId={selectedLeagueId}
              setSelectedLeagueId={(id) => {
                setSelectedLeagueId(id);
                if (!id) {
                  setLeagueTab("standings");
                  setLeagueEventosSubTab(undefined);
                }
              }}
              createLeagueMutation={createLeagueMutation}
              leagueTab={leagueTab}
              leagueEventosSubTab={leagueEventosSubTab}
              onLeagueTabChange={setLeagueTab}
              playerCountry={playerProfile?.country}
              playerNationalRank={playerProfile?.nationalRank}
            />
          </>
        )}

        {activeTab === "social" && (
          <div className="space-y-4">
            <ContextualTooltip section="social" tooltipsSeen={user?.tooltipsSeen} />
            {/* Compact Invite Bar */}
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center gap-2 min-w-0">
                <Share2 className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm font-medium">Invita amigos</span>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={async () => {
                    const inviteUrl = "https://courtvibe.app/invite";
                    if (hasNativeCopy()) {
                      const r = await nativeCopy(inviteUrl);
                      if (!r?.ok) {
                        await navigator.clipboard.writeText(inviteUrl).catch(() => {});
                      }
                    } else {
                      await navigator.clipboard.writeText(inviteUrl);
                    }
                    toast({
                      title: "¡Enlace copiado!",
                      description: "Comparte el enlace con tus amigos",
                    });
                  }}
                  data-testid="button-copy-invite"
                >
                  <Copy className="h-4 w-4" />
                </Button>
                {(hasNativeShare() || (typeof navigator !== "undefined" && "share" in navigator)) && (
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={async () => {
                      const payload = {
                        title: "CourtVibe - Tu app de pádel",
                        text: "¡Únete a CourtVibe! Registra partidos, compite en ligas y mejora tu nivel de pádel.",
                        url: "https://courtvibe.app/invite",
                      };
                      try {
                        if (hasNativeShare()) {
                          const r = await nativeShare(payload);
                          if (r?.ok || r?.cancelled) return;
                        }
                        if ("share" in navigator) {
                          await navigator.share(payload);
                        }
                      } catch (err) {
                        // User cancelled or share failed silently
                      }
                    }}
                    data-testid="button-share-invite"
                  >
                    <Share2 className="h-4 w-4 mr-1.5" />
                    Compartir
                  </Button>
                )}
              </div>
            </div>

            {/* Friend Requests - compact notification style */}
            <FriendRequests />

            {/* Tabs for Search/Friends on mobile, side-by-side on desktop */}
            <Tabs defaultValue="search" className="lg:hidden">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger
                  value="search"
                  className="text-sm"
                  data-testid="tab-search-players"
                >
                  <Search className="h-4 w-4 mr-1.5" />
                  Buscar
                </TabsTrigger>
                <TabsTrigger
                  value="friends"
                  className="text-sm"
                  data-testid="tab-friends-list"
                >
                  <Users className="h-4 w-4 mr-1.5" />
                  Amigos
                </TabsTrigger>
              </TabsList>
              <TabsContent value="search" className="mt-4 space-y-4">
                <SearchPlayers />
                <ActivityFeed />
              </TabsContent>
              <TabsContent value="friends" className="mt-4">
                <FriendsList />
              </TabsContent>
            </Tabs>

            {/* Side-by-side on desktop */}
            <div className="hidden lg:grid lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  Buscar Jugadores
                </h2>
                <SearchPlayers />
                <ActivityFeed />
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Tus Amigos
                </h2>
                <FriendsList />
              </div>
            </div>
          </div>
        )}

        {activeTab === "profile" && (
          <div className="grid lg:grid-cols-3 gap-6">
            <ContextualTooltip section="profile" tooltipsSeen={user?.tooltipsSeen} />
            <div className="space-y-4">
              <PlayerProfile
                name={
                  user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.email || "Jugador"
                }
                level={
                  levelHistory && levelHistory.length > 0
                    ? levelHistory[0].newLevel
                    : playerProfile?.skillLevel || 3.14
                }
                globalRank={playerProfile?.globalRank}
                nationalRank={playerProfile?.nationalRank}
                nationalRankBest={playerProfile?.nationalRankBest}
                playerCountry={playerProfile?.country}
                matchesPlayed={playerProfile?.matchesPlayed || 0}
                winRate={
                  playerProfile?.matchesPlayed &&
                  playerProfile.matchesPlayed > 0
                    ? Math.round(
                        ((playerProfile.matchesWon || 0) /
                          playerProfile.matchesPlayed) *
                          100,
                      )
                    : 0
                }
                memberSince="Nov 2025"
                profileImageUrl={user?.profileImageUrl}
                profileData={playerProfile}
                homeClubName={playerProfile?.homeClubName ?? null}
                onAddHomeClub={() => {
                  setActiveTab("profile");
                  setTimeout(
                    () => setLocation("/editar-perfil?focus=homeClub"),
                    0,
                  );
                }}
              />
              <EventAchievementsSection userId={user?.id} />
              {user && (
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setActiveTab("profile");
                      setTimeout(() => setLocation("/editar-perfil"), 0);
                    }}
                    data-testid="button-edit-profile"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Editar Perfil
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setLocation("/logros")}
                    data-testid="button-logros"
                  >
                    <Trophy className="h-4 w-4 mr-2" />
                    Logros
                  </Button>
                </div>
              )}
            </div>
            <div className="lg:col-span-2 space-y-6">
              {/* Level Evolution Chart */}
              <LevelEvolutionChart
                currentLevel={playerProfile?.skillLevel ?? 0}
                onInfoClick={() => setLocation("/como-funciona")}
              />

              {/* Match Statistics Summary */}
              <div className="space-y-6">
                {matchHistory.length > 0 && (
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="p-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                          {matchHistory.filter((m) => m.userWon).length}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Victorias
                        </p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                          {matchHistory.filter((m) => !m.userWon).length}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Derrotas
                        </p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-center">
                        <p className="text-2xl font-bold">
                          {matchHistory.length > 0
                            ? Math.round(
                                (matchHistory.filter((m) => m.userWon).length /
                                  matchHistory.length) *
                                  100,
                              )
                            : 0}
                          %
                        </p>
                        <p className="text-sm text-muted-foreground">Winrate</p>
                      </div>
                    </Card>
                    <Card className="p-4">
                      <div className="text-center">
                        <div className="flex items-center justify-center gap-1 mb-1">
                          <p className="text-2xl font-bold text-primary">
                            {Math.round(
                              1000 +
                                ((levelHistory && levelHistory.length > 0
                                  ? levelHistory[0].newLevel
                                  : (playerProfile?.skillLevel ?? 3.0)) -
                                  3) *
                                  150,
                            )}
                          </p>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 no-default-hover-elevate"
                              >
                                <HelpCircle className="h-3 w-3 text-muted-foreground" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Tu Puntuación</DialogTitle>
                                <DialogDescription className="space-y-4 pt-4">
                                  <p>
                                    La puntuación se calcula automáticamente
                                    basándose en tu nivel global actual:
                                  </p>
                                  <div className="bg-muted p-3 rounded-md font-mono text-xs text-center">
                                    Puntuación = 1000 + (Nivel - 3.0) × 150
                                  </div>
                                  <ul className="text-sm space-y-2 list-disc pl-4">
                                    <li>Nivel 1.0 = 700 pts</li>
                                    <li>Nivel 3.0 = 1000 pts</li>
                                    <li>Nivel 5.0 = 1300 pts</li>
                                  </ul>
                                  <p className="text-sm">
                                    Se actualiza después de cada partido
                                    aprobado para reflejar tu progreso real.
                                  </p>
                                  <Button
                                    className="w-full mt-4"
                                    onClick={() => {
                                      setLocation("/como-funciona");
                                    }}
                                  >
                                    Ver más detalles
                                  </Button>
                                </DialogDescription>
                              </DialogHeader>
                            </DialogContent>
                          </Dialog>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Puntuación
                        </p>
                      </div>
                    </Card>
                  </div>
                )}

                {/* Advanced Stats */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold">
                    Estadísticas Avanzadas
                  </h3>
                  {matchHistory.length === 0 ? (
                    <Card
                      className="p-6 text-center"
                      data-testid="card-no-matches"
                    >
                      <p className="text-sm text-muted-foreground mb-3">
                        Registra tus primeros partidos para ver tus estadísticas
                        avanzadas
                      </p>
                      <Button
                        onClick={() => setLocation("/registrar-partido")}
                        data-testid="button-record-first-match"
                      >
                        Registrar Partido
                      </Button>
                    </Card>
                  ) : (
                    <>
                      <AdvancedStatsCombo matchHistory={matchHistory} />
                      <HeadToHead matchHistory={matchHistory} />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <MobileNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        socialBadgeCount={socialBadgeCount}
      />

      {/* Match Summary Dialog */}
      <MatchSummaryDialog
        open={!!matchSummary}
        onClose={() => setMatchSummary(null)}
        summary={matchSummary}
      />

      {/* Delete Match Confirmation Dialog */}
      {matchToDelete && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Eliminar Partido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                ¿Estás seguro de que quieres eliminar este partido? Esta acción
                revertirá los cambios en tu nivel y estadísticas.
              </p>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setMatchToDelete(null)}
                  disabled={deleteMatchMutation.isPending}
                  data-testid="button-cancel-delete"
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => {
                    deleteMatchMutation.mutate(
                      {
                        matchId: matchToDelete.id,
                        matchType: matchToDelete.type,
                        leagueId: matchToDelete.leagueId,
                      },
                      {
                        onSettled: () => setMatchToDelete(null),
                      },
                    );
                  }}
                  disabled={deleteMatchMutation.isPending}
                  data-testid="button-confirm-delete"
                >
                  {deleteMatchMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Eliminar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function OnboardingFlow() {
  const { user, refetchUser } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const createPlayerProfile = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/profile/player", data);
      return response.json();
    },
    onSuccess: () => {
      track("onboarding_completed", { userType: "player", method: "replit" });
      toast({
        title: "¡Perfil creado!",
        description: "Ya puedes empezar a usar CourtVibe",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      refetchUser();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo crear el perfil",
        variant: "destructive",
      });
    },
  });

  const createClubProfile = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/profile/club", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "¡Club registrado!",
        description: "Ya puedes empezar a gestionar tu club",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      refetchUser();
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo crear el perfil del club",
        variant: "destructive",
      });
    },
  });

  if (user?.userType === "club") {
    return (
      <ClubOnboarding
        onComplete={(data) => createClubProfile.mutate(data)}
        isSubmitting={createClubProfile.isPending}
        clubEmail={user.email || undefined}
      />
    );
  }

  return (
    <PlayerOnboarding
      onComplete={(data) => createPlayerProfile.mutate(data)}
      isSubmitting={createPlayerProfile.isPending}
      userName={user?.firstName || undefined}
      onCancel={() => navigate("/")}
    />
  );
}

function Router() {
  const { isAuthenticated, isLoading, needsOnboarding, refetchUser } =
    useAuth();
  const devModeAttemptedRef = useRef(false);
  const [location] = useLocation();
  const previousLocationRef = useRef<string | null>(null);

  // Track in-app navigation so pages can distinguish "user navigated here
  // from elsewhere in the app" (safe to `history.back()`) from "user
  // landed directly on this URL" (e.g. a shared link opened from
  // WhatsApp/a notification, where there is nothing to go back to). See
  // `lib/navigationHistory.ts` for why this can't just be `history.length`.
  useEffect(() => {
    if (previousLocationRef.current !== null && previousLocationRef.current !== location) {
      markInternalNavigation();
    }
    previousLocationRef.current = location;
  }, [location]);

  // Auto-login in dev mode
  useEffect(() => {
    const devModeEnabled = import.meta.env.VITE_DEV_MODE === "true";

    if (
      devModeEnabled &&
      !isAuthenticated &&
      !isLoading &&
      !devModeAttemptedRef.current
    ) {
      devModeAttemptedRef.current = true;
      fetch("/api/dev-login", { credentials: "include" })
        .then((res) => {
          if (res.ok) {
            refetchUser();
          }
        })
        .catch((err) => console.error("Dev login error:", err));
    }
  }, [isAuthenticated, isLoading, refetchUser]);

  useAnalyticsIdentify();
  useSmartPolling(isAuthenticated);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Trophy className="h-12 w-12 text-primary mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const unauthDefs = ROUTE_DEFINITIONS.filter((r) => r.scope === "shared");
    return (
      <Switch>
        {unauthDefs.map(({ key, path, component: C }) => (
          <Route key={key} path={path} component={C} />
        ))}
      </Switch>
    );
  }

  if (needsOnboarding) {
    return <OnboardingFlow />;
  }

  const authDefs = [
    ...ROUTE_DEFINITIONS.filter(
      (r) => r.scope === "shared" && r.key !== "root-unauth",
    ),
    ...ROUTE_DEFINITIONS.filter((r) => r.scope === "auth-only"),
  ];
  return (
    <Switch>
      {authDefs.map(({ key, path, component: C }) => (
        <Route key={key} path={path} component={C} />
      ))}
    </Switch>
  );
}

const AUTO_APPROVAL_HOURS = 3;

function formatDeadlineTimeWeb(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const deadline = new Date(new Date(createdAt).getTime() + AUTO_APPROVAL_HOURS * 60 * 60 * 1000);
  if (deadline <= new Date()) return null;
  return deadline.toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
            <Toaster />
            <PWAInstallPrompt />
            <Router />
          </WouterRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
